import * as Api from '@ViewModels';
import { keepPreviousData, QueryClient, useQuery } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';

export const ADMIN_ACCOUNT_USERS_QUERY_KEY = 'adminAccountUsers';

export interface AdminAccountUsersPage {
	values: Api.IUserWithStats[];
	totalCount: number;
	page: number;
	pageSize: number;
}

interface UseAdminAccountUsersQueryParams {
	accountId: string;
	page: number;
	pageSize?: number;
	statuses: string[];
	roles: string[];
	search: string;
}

export const useAdminAccountUsersQuery = ({
	accountId,
	page,
	pageSize = 50,
	statuses,
	roles,
	search,
}: UseAdminAccountUsersQueryParams) => {
	const userSession = useUserSession();

	return useQuery<AdminAccountUsersPage>({
		enabled: !!accountId,
		placeholderData: keepPreviousData,
		staleTime: 30_000,
		queryKey: [ADMIN_ACCOUNT_USERS_QUERY_KEY, accountId, page, pageSize, { statuses, roles, search }],
		queryFn: async () => {
			const params = new URLSearchParams();
			params.set('accountId', accountId);
			params.set('page', String(page));
			params.set('pageSize', String(pageSize));
			params.set('expand', 'Stats');
			for (const s of statuses) {
				params.append('status', s);
			}
			for (const r of roles) {
				params.append('role', r);
			}
			if (search.trim()) {
				params.set('search', search.trim());
			}

			return userSession.webServiceHelper.callAsync<AdminAccountUsersPage>(
				`admin/userOperations/byAccount/paginated?${params.toString()}`,
				'GET'
			);
		},
	});
};

export const invalidateAdminAccountUsersQuery = (queryClient: QueryClient, accountId: string) => {
	queryClient.invalidateQueries({ queryKey: [ADMIN_ACCOUNT_USERS_QUERY_KEY, accountId] });
};
